import { ColDef } from '@ag-grid-enterprise/all-modules'
import { AgGridColumnType, AgGridExportType, ExportedColDef } from '@/shared/models/ag-grid.model'
import i18n from '@/lang/i18n'
import { getEntityStatusStateColumnGridOptions } from '@/grid/base-grid-options'
import buildIconHeaderTemplate from '@/grid/buildIconHeaderTemplate'
import IncidentRenderer from '@/cellRenderers/common/IncidentRenderer.vue'
import AttachmentCountRenderer from '@/cellRenderers/common/AttachmentCountRenderer.vue'
import { ARCHIVE_TASK } from '@/modules/archiveModule/sideBar/sideBarOptions'
import archiveFilterService from '@/modules/archiveModule/services/archiveFilterService'

export function getColumnDefs(taskId: number): (ColDef | ExportedColDef)[] {
    const columnDefs: { [key: string]: (ColDef | ExportedColDef)[] } = {
        /// PENDING TO BE ARCHIVED
        [ARCHIVE_TASK.SAMPLES_TO_BE_ARCHIVED.id]: archiveGridSamplesPendingToBeArchivedColumnDefs(),
        [ARCHIVE_TASK.PORTIONS_TO_BE_ARCHIVED.id]: archiveGridPortionsPendingToBeArchivedColumnDefs(),
        [ARCHIVE_TASK.SUBPORTIONS_TO_BE_ARCHIVED.id]: archiveGridSubportionsPendingToBeArchivedColumnDefs(),
        /// PENDING TO BE UNARCHIVED
        [ARCHIVE_TASK.SAMPLES_TO_BE_UNARCHIVED.id]: archiveGridSamplesPendingToBeUnarchivedColumnDefs(),
        [ARCHIVE_TASK.PORTIONS_TO_BE_UNARCHIVED.id]: archiveGridPortionsPendingToBeUnarchivedColumnDefs(),
        [ARCHIVE_TASK.SUBPORTIONS_TO_BE_UNARCHIVED.id]: archiveGridSubportionsPendingToBeUnarchivedColumnDefs(),
        /// ARCHIVED
        [ARCHIVE_TASK.ARCHIVED_SAMPLES.id]: archiveGridArchivedSamplesColumnDefs(),
        [ARCHIVE_TASK.ARCHIVED_PORTIONS.id]: archiveGridArchivedPortionsColumnDefs(),
        [ARCHIVE_TASK.ARCHIVED_SUBPORTIONS.id]: archiveGridArchivedSubportionsColumnDefs()
    }
    return columnDefs[taskId] ?? []
}

enum ArchiveGridColumnDefType {
    CUSTOM_CHECKBOX = 'CUSTOM_CHECKBOX',
    SAMPLE_LABEL = 'SAMPLE_LABEL',
    BLOCK_LABEL = 'BLOCK_LABEL',
    SLIDE_LABEL = 'SLIDE_LABEL',
    SAMPLE_UNARCHIVING_REASON = 'SAMPLE_UNARCHIVING_REASON',
    PORTION_UNARCHIVING_REASON = 'PORTION_UNARCHIVING_REASON',
    SUBPORTION_UNARCHIVING_REASON = 'SUBPORTION_UNARCHIVING_REASON',
    LOCATION_LABEL = 'LOCATION_LABEL',
    LAST_ARCHIVING_DATE = 'LAST_ARCHIVING_DATE',
    LAST_UNARCHIVING_DATE = 'LAST_UNARCHIVING_DATE',
    LAST_ARCHIVING_RESPONSIBLE = 'LAST_ARCHIVING_RESPONSIBLE',
    LAST_UNARCHIVING_RESPONSIBLE = 'LAST_UNARCHIVING_RESPONSIBLE',
    DEPLETED = 'DEPLETED',
    INCIDENTS = 'INCIDENTS',
    ATTACHMENTS = 'ATTACHMENTS',
    PRIORITY = 'PRIORITY'
}

type ArchiveGridColumnDefs = { [key in keyof typeof ArchiveGridColumnDefType]: ColDef | ExportedColDef }

function buildColumnDefs(): ArchiveGridColumnDefs {
    return {
        [ArchiveGridColumnDefType.CUSTOM_CHECKBOX]: { type: AgGridColumnType.CUSTOM_CHECKBOX },
        [ArchiveGridColumnDefType.SAMPLE_LABEL]: {
            headerName: i18n.tc('grid.headerName.sampleLabel'),
            colId: 'samLabel',
            field: 'label',
            type: AgGridColumnType.TEXT,
            exportType: AgGridExportType.TEXT,
            useForGeneratingBarcodeInExports: true,
            flex: 1,
            minWidth: 80
        },
        [ArchiveGridColumnDefType.BLOCK_LABEL]: {
            headerName: i18n.tc('grid.headerName.blockLabel'),
            colId: 'porLabel',
            field: 'label',
            type: AgGridColumnType.TEXT,
            exportType: AgGridExportType.TEXT,
            useForGeneratingBarcodeInExports: true,
            flex: 1,
            minWidth: 80
        },
        [ArchiveGridColumnDefType.SLIDE_LABEL]: {
            headerName: i18n.tc('grid.headerName.slideLabel'),
            colId: 'supLabel',
            field: 'label',
            type: AgGridColumnType.TEXT,
            exportType: AgGridExportType.TEXT,
            useForGeneratingBarcodeInExports: true,
            flex: 1,
            minWidth: 80
        },
        [ArchiveGridColumnDefType.SAMPLE_UNARCHIVING_REASON]: {
            ...getEntityStatusStateColumnGridOptions(archiveFilterService.getSamplePendingUnarchivingReasons()),
            headerName: i18n.tc('archive.grid.unarchivingReason'),
            field: 'pendingUnarchivingReason',
            exportType: AgGridExportType.TEXT,
            sortable: false
        },
        [ArchiveGridColumnDefType.PORTION_UNARCHIVING_REASON]: {
            ...getEntityStatusStateColumnGridOptions(archiveFilterService.getPortionPendingUnarchivingReasons()),
            headerName: i18n.tc('archive.grid.unarchivingReason'),
            field: 'pendingUnarchivingReason',
            exportType: AgGridExportType.TEXT,
            sortable: false
        },
        [ArchiveGridColumnDefType.SUBPORTION_UNARCHIVING_REASON]: {
            ...getEntityStatusStateColumnGridOptions(archiveFilterService.getSubportionPendingUnarchivingReasons()),
            headerName: i18n.tc('archive.grid.unarchivingReason'),
            field: 'pendingUnarchivingReason',
            exportType: AgGridExportType.TEXT,
            sortable: false
        },
        [ArchiveGridColumnDefType.LOCATION_LABEL]: {
            headerName: i18n.tc('archive.grid.archiveLocation'),
            colId: 'locationLabel',
            field: 'archivingLocation',
            type: AgGridColumnType.TEXT,
            exportType: AgGridExportType.TEXT,
            flex: 1,
            minWidth: 180
        },
        [ArchiveGridColumnDefType.LAST_ARCHIVING_DATE]: {
            headerName: i18n.tc('archive.grid.inputDate'),
            field: 'lastArchivingDate',
            type: AgGridColumnType.DATETIME,
            exportType: AgGridExportType.DATETIME,
            flex: 1,
            minWidth: 100
        },
        [ArchiveGridColumnDefType.LAST_UNARCHIVING_DATE]: {
            headerName: i18n.tc('archive.grid.outputDate'),
            field: 'lastUnarchivingDate',
            type: AgGridColumnType.DATETIME,
            exportType: AgGridExportType.DATETIME,
            flex: 1,
            minWidth: 100
        },
        [ArchiveGridColumnDefType.LAST_ARCHIVING_RESPONSIBLE]: {
            headerName: i18n.tc('archive.grid.inputUser'),
            colId: 'responsibleForLastArchiving.normalizedFullName',
            field: 'responsibleForLastArchiving.fullName',
            type: AgGridColumnType.TEXT,
            exportType: AgGridExportType.TEXT,
            flex: 1,
            minWidth: 180
        },
        [ArchiveGridColumnDefType.LAST_UNARCHIVING_RESPONSIBLE]: {
            headerName: i18n.tc('archive.grid.outputUser'),
            colId: 'responsibleForLastUnarchiving.normalizedFullName',
            field: 'responsibleForLastUnarchiving.fullName',
            type: AgGridColumnType.TEXT,
            exportType: AgGridExportType.TEXT,
            flex: 1,
            minWidth: 180
        },
        [ArchiveGridColumnDefType.DEPLETED]: {
            headerName: i18n.tc('grid.detail.depletedBlock'),
            field: 'depleted',
            type: AgGridColumnType.CHECK_ICON,
            exportType: AgGridExportType.BOOLEAN,
            flex: 1,
            minWidth: 120
        },
        [ArchiveGridColumnDefType.INCIDENTS]: {
            headerName: i18n.tc('archive.grid.incidents'),
            headerTooltip: i18n.tc('archive.grid.incidents'),
            headerComponentParams: {
                template: buildIconHeaderTemplate('far fa-exclamation-triangle')
            },
            field: 'incidentsCount',
            type: AgGridColumnType.INCIDENT,
            exportType: AgGridExportType.INTEGER,
            cellRendererFramework: IncidentRenderer,
            filter: false,
            sortable: false,
            flex: 1,
            maxWidth: 80
        },
        [ArchiveGridColumnDefType.ATTACHMENTS]: {
            headerName: i18n.tc('archive.grid.attachments'),
            headerTooltip: i18n.tc('archive.grid.attachments'),
            headerComponentParams: {
                template: buildIconHeaderTemplate('far fa-paperclip')
            },
            field: 'attachmentsCount',
            type: AgGridColumnType.TEXT,
            exportType: AgGridExportType.INTEGER,
            cellRendererFramework: AttachmentCountRenderer,
            filter: false,
            sortable: false,
            flex: 1,
            maxWidth: 80
        },
        [ArchiveGridColumnDefType.PRIORITY]: {
            headerName: i18n.tc('global.priority'),
            colId: 'priority.level',
            field: 'priority',
            type: AgGridColumnType.PRIORITY,
            exportField: 'priority.name',
            exportType: AgGridExportType.TEXT,
            flex: 1
        }
    }
}

/// PENDING TO BE ARCHIVED
export function archiveGridSamplesPendingToBeArchivedColumnDefs(): (ColDef | ExportedColDef)[] {
    const columnDefs = buildColumnDefs()
    return [
        columnDefs.CUSTOM_CHECKBOX,
        columnDefs.SAMPLE_LABEL,
        columnDefs.LAST_ARCHIVING_DATE,
        columnDefs.LAST_UNARCHIVING_DATE,
        columnDefs.LAST_ARCHIVING_RESPONSIBLE,
        columnDefs.LAST_UNARCHIVING_RESPONSIBLE,
        columnDefs.INCIDENTS,
        columnDefs.ATTACHMENTS
    ]
}
export function archiveGridPortionsPendingToBeArchivedColumnDefs(): (ColDef | ExportedColDef)[] {
    const columnDefs = buildColumnDefs()
    return [
        columnDefs.CUSTOM_CHECKBOX,
        columnDefs.BLOCK_LABEL,
        columnDefs.LAST_ARCHIVING_DATE,
        columnDefs.LAST_UNARCHIVING_DATE,
        columnDefs.LAST_ARCHIVING_RESPONSIBLE,
        columnDefs.LAST_UNARCHIVING_RESPONSIBLE,
        columnDefs.DEPLETED,
        columnDefs.INCIDENTS,
        columnDefs.ATTACHMENTS
    ]
}
export function archiveGridSubportionsPendingToBeArchivedColumnDefs(): (ColDef | ExportedColDef)[] {
    const columnDefs = buildColumnDefs()
    return [
        columnDefs.CUSTOM_CHECKBOX,
        columnDefs.SLIDE_LABEL,
        columnDefs.LAST_ARCHIVING_DATE,
        columnDefs.LAST_UNARCHIVING_DATE,
        columnDefs.LAST_ARCHIVING_RESPONSIBLE,
        columnDefs.LAST_UNARCHIVING_RESPONSIBLE,
        columnDefs.INCIDENTS,
        columnDefs.ATTACHMENTS,
        columnDefs.PRIORITY
    ]
}

/// PENDING TO BE UNARCHIVED
export function archiveGridSamplesPendingToBeUnarchivedColumnDefs(): (ColDef | ExportedColDef)[] {
    const columnDefs = buildColumnDefs()
    return [
        columnDefs.CUSTOM_CHECKBOX,
        columnDefs.SAMPLE_LABEL,
        columnDefs.LOCATION_LABEL,
        columnDefs.SAMPLE_UNARCHIVING_REASON,
        columnDefs.LAST_ARCHIVING_DATE,
        columnDefs.LAST_UNARCHIVING_DATE,
        columnDefs.LAST_ARCHIVING_RESPONSIBLE,
        columnDefs.LAST_UNARCHIVING_RESPONSIBLE,
        columnDefs.INCIDENTS,
        columnDefs.ATTACHMENTS
    ]
}
export function archiveGridPortionsPendingToBeUnarchivedColumnDefs(): (ColDef | ExportedColDef)[] {
    const columnDefs = buildColumnDefs()
    return [
        columnDefs.CUSTOM_CHECKBOX,
        columnDefs.BLOCK_LABEL,
        columnDefs.LOCATION_LABEL,
        columnDefs.PORTION_UNARCHIVING_REASON,
        columnDefs.LAST_ARCHIVING_DATE,
        columnDefs.LAST_UNARCHIVING_DATE,
        columnDefs.LAST_ARCHIVING_RESPONSIBLE,
        columnDefs.LAST_UNARCHIVING_RESPONSIBLE,
        columnDefs.DEPLETED,
        columnDefs.INCIDENTS,
        columnDefs.ATTACHMENTS
    ]
}
export function archiveGridSubportionsPendingToBeUnarchivedColumnDefs(): (ColDef | ExportedColDef)[] {
    const columnDefs = buildColumnDefs()
    return [
        columnDefs.CUSTOM_CHECKBOX,
        columnDefs.SLIDE_LABEL,
        columnDefs.LOCATION_LABEL,
        columnDefs.SUBPORTION_UNARCHIVING_REASON,
        columnDefs.LAST_ARCHIVING_DATE,
        columnDefs.LAST_UNARCHIVING_DATE,
        columnDefs.LAST_ARCHIVING_RESPONSIBLE,
        columnDefs.LAST_UNARCHIVING_RESPONSIBLE,
        columnDefs.INCIDENTS,
        columnDefs.ATTACHMENTS,
        columnDefs.PRIORITY
    ]
}

/// ARCHIVED
export function archiveGridArchivedSamplesColumnDefs(): (ColDef | ExportedColDef)[] {
    const columnDefs = buildColumnDefs()
    return [
        columnDefs.CUSTOM_CHECKBOX,
        columnDefs.SAMPLE_LABEL,
        columnDefs.LOCATION_LABEL,
        columnDefs.SAMPLE_UNARCHIVING_REASON,
        columnDefs.LAST_ARCHIVING_DATE,
        columnDefs.LAST_UNARCHIVING_DATE,
        columnDefs.LAST_ARCHIVING_RESPONSIBLE,
        columnDefs.LAST_UNARCHIVING_RESPONSIBLE,
        columnDefs.INCIDENTS,
        columnDefs.ATTACHMENTS
    ]
}
export function archiveGridArchivedPortionsColumnDefs(): (ColDef | ExportedColDef)[] {
    const columnDefs = buildColumnDefs()
    return [
        columnDefs.CUSTOM_CHECKBOX,
        columnDefs.BLOCK_LABEL,
        columnDefs.LOCATION_LABEL,
        columnDefs.PORTION_UNARCHIVING_REASON,
        columnDefs.LAST_ARCHIVING_DATE,
        columnDefs.LAST_UNARCHIVING_DATE,
        columnDefs.LAST_ARCHIVING_RESPONSIBLE,
        columnDefs.LAST_UNARCHIVING_RESPONSIBLE,
        columnDefs.DEPLETED,
        columnDefs.INCIDENTS,
        columnDefs.ATTACHMENTS
    ]
}
export function archiveGridArchivedSubportionsColumnDefs(): (ColDef | ExportedColDef)[] {
    const columnDefs = buildColumnDefs()
    return [
        columnDefs.CUSTOM_CHECKBOX,
        columnDefs.SLIDE_LABEL,
        columnDefs.LOCATION_LABEL,
        columnDefs.SUBPORTION_UNARCHIVING_REASON,
        columnDefs.LAST_ARCHIVING_DATE,
        columnDefs.LAST_UNARCHIVING_DATE,
        columnDefs.LAST_ARCHIVING_RESPONSIBLE,
        columnDefs.LAST_UNARCHIVING_RESPONSIBLE,
        columnDefs.INCIDENTS,
        columnDefs.ATTACHMENTS,
        columnDefs.PRIORITY
    ]
}
